import React from "react";
import { ChevronDown } from "lucide-react";

interface FAQItem {
  question: string;
  answer: string;
}

const faqs: FAQItem[] = [
  {
    question: "What are Cold Case Game Kits?",
    answer:
      "Cold Case Game Kits are interactive mystery games based on fictional cold cases. Each kit contains all the materials needed to investigate and solve a complex crime story, including evidence files, witness statements, police reports, photos, and more.",
  },
  {
    question: "How long does it take to solve a case?",
    answer:
      "Most cases take 60-90 minutes to solve, depending on the complexity and your group's pace.",
  },
  {
    question: "How many people can play together?",
    answer:
      "Our kits are designed for 1-8 players, with the optimal group size being 2-6 players. Solo play is possible but may be more challenging as you won't have others to discuss theories with.",
  },
  {
    question: "Are the cases replayable?",
    answer:
      "While the main solution remains the same, many players enjoy replaying cases to discover missed clues or alternative theories. However, the primary reveal will be known after the first playthrough.",
  },
  {
    question: "What do you get with a 'Buy a Mystery' purchase?",
    answer:
      "Every purchase includes an instantly available PDF digital download. It has the story, all the evidence files, witness statements, police reports, photos, and more. It is a single file that can be printed out or viewed on a computer, tablet, or phone. If you purchase a case, you will receive a download link via email.",
  },
  {
    question: "What do you get with a 'Create a Mystery' purchase?",
    answer:
      "Every purchase includes a PDF digital download that will take 6-24 hours to generate. If you need it sooner, you can select a pre-made, instantly available 'Buy a Mystery' purchase. Each custom case contains a PDF digital download that has the background story, all the evidence files, witness statements, police reports, photos, and more. It is a single file that can be printed out or viewed on a computer, tablet, or phone. If you purchase 'Create a Mystery' case, you will receive a download link via email within 24 hours of purchase.",
  },
  {
    question: "Are the cases suitable for children?",
    answer:
      "Our cases are designed for teens and adults (13+) due to complex storylines and mature themes. While not graphically violent, they deal with serious topics typical of real cold cases.",
  },
  {
    question: "What's the refund policy?",
    answer:
      "Due to the digital nature of our products, we generally don't offer refunds after the files have been downloaded. However, we're happy to help if you experience technical issues.",
  },
  {
    question: "What does sponsoring a case mean?",
    answer:
      "When you sponsor a case, you begin to earn credits each time someone downloads your sponsored case. Each download earns 1 credit and it takes 10 credits to download a new case. You earn 5 credits for each case you sponsor. It's a great way to earn credits for future purchases!",
  },
  {
    question: "Can I sponsor multiple cases?",
    answer:
      "Yes, you can sponsor as many cases as you want. However, there is only one sponsor per case. Once all cases are sponsored, you cannot sponsor any more cases.",
  },
  {
    question: "How do credits work?",
    answer:
      "Credits can be earned through sponsorships. It takes 10 credits to download any case. Credits expire 24 months after acquisition. When you sponsor a case, you begin to earn credits each time someone downloads your sponsored case. Each download earns 1 credit and it takes 10 credits to download a new case. You initially earn 5 credits for each case you sponsor. It's a great way to earn credits for future purchases!",
  },
  {
    question: "Can I convert my credits to cash?",
    answer:
      "No, credits are not convertible to cash. They are only used to purchase cases.",
  },

  {
    question: "How many credits can I expect to earn?",
    answer:
      "It's hard to say. It depends on how many people download your sponsored cases and how many cases you sponsor. However, you can expect to initially earn 5 credits for each case you sponsor. So, sponsoring 2 cases will earn you 10 credits enough to download another case for free.",
  },
  {
    question: "Should I buy or sponsor a case?",
    answer:
      "If you are planning on purchasing more than 2 cases, you can sponsor 2 cases and earn 10 credits right away. Therefore, you will get 3 cases for less than the price of buying 3 non-sponsored cases, but you have the benefits of sponsoring 2 cases which will earn you more credits for future purchases.",
  },
  {
    question: "Can I share my purchased case with friends?",
    answer:
      "Each purchase is for personal use only. If you want to play with friends, they should be physically present to share the materials. Sharing digital files is not permitted, but you can share the physical files you print out with friends.",
  },
  {
    question: "Do I need special software to use the digital files?",
    answer:
      "Our digital files are standard PDFs that can be opened with any PDF reader or browser. We recommend Adobe Reader or Preview (Mac) for the best experience.",
  },
  {
    question: "Are the cases based on real events?",
    answer:
      "All cases are fictional, the stories, names, locations, and details are all made up through our advanced AI Mystery Generator. This allows us to create more engaging and satisfying mysteries.",
  },
  {
    question: "What materials do I need to play?",
    answer:
      "Basic materials like paper, pens, and note-taking supplies are helpful. For digital play, a device to view PDFs is required. Ideally, a printer is also helpful.",
  },
  {
    question: "Can I host these for events or team building?",
    answer:
      "Yes! Many of our cases work great for events. However, you are not permitted to sell the cases to others or charge for them.",
  },
  {
    question: "How difficult are the cases?",
    answer:
      "Cases vary in difficulty but are designed to be solvable by a group of 3-5 players. Some cases are more complex than others. We provide a detailed explanation of the solution in the case file if you get stuck.",
  },
  {
    question: "Do you offer hints if we get stuck?",
    answer:
      "Yes, there is a specific person of interest in each case, who may or may not be the person who committed the crime. If you get stuck, you can see how the person of interest is exonerated or convicted.",
  },
  {
    question: "Are new cases added regularly?",
    answer:
      "We release new cases regularly, with special themed cases during holidays. Subscribers and social media followers get early access to new releases. You can join our mailing list to get notified when new cases are released.",
  },
  {
    question: "What payment methods do you accept?",
    answer:
      "We use FastSpring, a secure MoR payment provider to process payments, which accepts all major credit cards. All transactions are secure and encrypted. We do not store any payment information.",
  },
  {
    question: "Can I gift a case to someone?",
    answer:
      "This functionality is not yet available. We are working on it! Join our mailing list to get notified when this feature is available.",
  },
  {
    question: "What if I have technical issues?",
    answer:
      "Our support team is available via email to help with any technical issues. Most problems can be resolved within 24 hours.",
  },
  ,
  {
    question: "I don't have access to a printer, can I still play?",
    answer: (
      <div className="space-y-2">
        <p>
          Having access to a color printer is the ideal scenario as it&apos;s
          better to layout all the evidence over a table; however, you still can
          scroll through the pages of the PDF file on your computer but it will
          be more time consuming. Alternatively, you can use Staples printing
          services:
        </p>
        <div className="flex flex-col space-y-2">
          <a
            href="https://www.staples.com/services/printing/copies-documents-printing/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-crimson hover:text-crimson-dark"
          >
            <span className="mr-2">🇺🇸</span>
            <span className="underline">Staples US Printing Services</span>
          </a>
          <a
            href="https://www.staplescopyandprint.ca/PrintOnlineInfo/FileSubmissionInfo.aspx"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-crimson hover:text-crimson-dark"
          >
            <span className="mr-2">🇨🇦</span>
            <span className="underline">Staples Canada Printing Services</span>
          </a>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Note: Additional charges will apply for in-store printing services.
        </p>
      </div>
    ),
  },
  {
    question: "Do you offer a physical version (Hard Copy) of the cases?",
    answer:
      "Yes, we offer a physical version of the cases. Expect to receive your physical case within 1-3 weeks of purchase.",
  },
  {
    question: "How long does it take to receive a physical version (Hard Copy) of the cases?",
    answer:
      "We ship standard Canada Post. Typically within 1 week in Canada, 2-3 weeks internationally. Check out their site to see delivery times.",
  },
];

const FAQPage: React.FC = () => {
  const [openIndex, setOpenIndex] = React.useState<number | null>(null);

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <h1 className="text-4xl font-bold text-center mb-8 font-heading spotlight-text">
        Frequently Asked Questions
      </h1>
      <div className="space-y-4">
        {faqs.map((faq, index) => (
          <div key={index} className="border rounded-lg overflow-hidden">
            <button
              className="w-full flex items-center justify-between p-4 text-left bg-card hover:bg-muted/50"
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
            >
              <span className="font-medium">{faq.question}</span>
              <ChevronDown
                className={`h-5 w-5 transition-transform ${
                  openIndex === index ? "transform rotate-180" : ""
                }`}
              />
            </button>
            {openIndex === index && (
              <div className="p-4 bg-muted/30 border-t">
                <p className="text-muted-foreground">{faq.answer}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default FAQPage;
